import { gql } from "@codesandbox/api";
import { useEditorStore } from "components/Editor/useEditorStore";
import { CopyButton } from "components/Embeds/CopyButton";
import { useConvertToDevboxModal } from "components/GlobalModal/useConvertToDevboxModal";
import { env } from "environment-interface/env";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Icon,
  Input,
  Paragraph,
  ScrollArea,
  Select,
  Stack,
  Text,
  keyframes,
  styled,
} from "prism-react";
import { useEffect, useState } from "react";

import { useAnalytics } from "hooks/useAnalytics";

const PERMISSIONS: Array<{ text: string; value: gql.LiveSessionPermission }> = [
  {
    text: "Editor",
    value: gql.LiveSessionPermission.WRITE,
  },
  {
    text: "Read-only",
    value: gql.LiveSessionPermission.READ,
  },
];

export const LiveSessionSettings: React.FC<{ disabled: boolean }> = ({
  disabled,
}) => {
  const { liveSession } = useEditorStore();
  const connected = liveSession.state.status === "CONNECTED";
  const loading = liveSession.state.status === "CREATING";
  const copyLink = connected
    ? `${window.location.origin + (env.PUBLIC_BASE_PATH ?? "")}/live/${
        liveSession.state.session.id
      }`
    : "";

  const [copied, setCopied] = useState(false);
  const convertModal = useConvertToDevboxModal();
  const track = useAnalytics();

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => {
        setCopied(false);
      }, 2000);

      return () => {
        clearInterval(timer);
      };
    }
  }, [copied]);

  const mainButtonHandler = () => {
    if (connected) {
      liveSession.stop();
    } else {
      liveSession.start();
    }
  };

  if (disabled) {
    return (
      <Stack
        css={{
          borderLeft: "1px solid $neutral-bg-high",
          padding: "$5",
          height: "100%",
        }}
        gap={4}
      >
        <Stack css={{ height: "100%" }}>
          <Stack horizontal>
            <LiveIcon connected={connected} size={3} type="circleFull" />
            <Text>Live session</Text>
          </Stack>

          <Stack css={{ flex: 1, justifyContent: "center" }}>
            <Paragraph
              css={{
                color: "$neutral-fg-base",
                textAlign: "center",
                textWrap: "balance",
              }}
            >
              To use live sessions, you need to convert this Sandbox to a
              Devbox.
            </Paragraph>

            <Box
              css={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <span>
                <Button
                  icon="server"
                  onClick={() => {
                    convertModal.open();
                    track("convert_to_devbox_open", {
                      location: "live-session",
                    });
                  }}
                  variant="secondary"
                >
                  Convert to Devbox
                </Button>
              </span>
            </Box>
          </Stack>
        </Stack>
      </Stack>
    );
  }

  const renderGuests = connected ? (
    <Stack gap={0}>
      {liveSession.state.session.guests.map((guest) => (
        <Stack
          key={guest.userId}
          css={{ justifyContent: "space-between" }}
          horizontal
        >
          <Stack horizontal>
            <Avatar
              name={guest.username}
              shape="circle"
              size="s"
              src={guest.avatarUrl}
            />

            <Text>{guest.username}</Text>
          </Stack>

          <Select
            defaultValue={guest.permission}
            onValueChange={(value) => {
              liveSession.setGuestPermission({
                userId: guest.userId,
                permission: value as gql.LiveSessionPermission,
              });
            }}
          >
            {PERMISSIONS.map(({ text, value }) => (
              <Select.Item key={value} value={value}>
                {text}
              </Select.Item>
            ))}
          </Select>
        </Stack>
      ))}
    </Stack>
  ) : null;

  return (
    <Stack
      css={{
        borderLeft: "1px solid $neutral-bg-high",
        padding: "$5",
        height: "100%",
      }}
      gap={4}
    >
      <Stack>
        <Stack horizontal>
          <LiveIcon connected={connected} size={3} type="circleFull" />
          <Text>Live session</Text>
        </Stack>

        <Paragraph css={{ color: "$neutral-fg-base" }}>
          {connected
            ? "You're live! Share this link with others to invite them to join."
            : "Host a live coding session with real-time code collaboration."}
        </Paragraph>
      </Stack>

      {connected && (
        <Stack css={{ width: "100%" }} gap={0} horizontal>
          <Input
            css={{
              flex: 1,
              borderRadius: "$2 0 0 $2",
              border: "1px solid $neutral-bg-high",
              borderRight: "none",
            }}
            onClick={(e) => {
              const inputElement = e.target as HTMLInputElement;
              inputElement.select();
            }}
            value={copyLink}
            readOnly
          />

          <CopyButton
            content={copyLink}
            css={{
              borderRadius: "0 $2 $2 0",
            }}
            type="share"
            variant="secondary"
          >
            Copy
          </CopyButton>
        </Stack>
      )}

      <Stack>
        <Text>Default guest permission</Text>
        <Select
          defaultValue={liveSession.state.defaultPermission}
          onValueChange={(value) => {
            liveSession.setDefaultPermision(value as gql.LiveSessionPermission);
          }}
          variant="solid"
        >
          {PERMISSIONS.map(({ text, value }) => (
            <Select.Item key={value} value={value}>
              {text}
            </Select.Item>
          ))}
        </Select>
      </Stack>

      <Stack horizontal>
        <Checkbox
          checked={liveSession.state.notifyWhenGuestsJoin}
          id="notify"
          onCheckedChange={(event) =>
            liveSession.setNotifyWhenGuestsJoin(Boolean(event))
          }
        />

        <Text as="label" htmlFor="notify">
          Get notified when a new guest joins
        </Text>
      </Stack>

      <Button loading={loading} onClick={mainButtonHandler} variant="primary">
        {connected ? "Stop session" : "Go live"}
      </Button>

      {connected && (
        <Stack>
          <Text>Guests ({liveSession.state.session.guests.length})</Text>

          {liveSession.state.session.guests.length > 5 ? (
            <ScrollArea
              css={{
                height: 130,
                "--scrollbar-fade-color": "var(--colors-neutral-bg-medium)",
              }}
              verticalFade
            >
              {renderGuests}
            </ScrollArea>
          ) : (
            renderGuests
          )}
        </Stack>
      )}
    </Stack>
  );
};

export const LiveIcon = styled(Icon, {
  color: "$informative-success-base",
  transition: "color 200ms ease",

  variants: {
    connected: {
      true: {
        color: "$informative-error-base",

        animation: `${keyframes({
          "0%": { opacity: 1 },
          "50%": {
            opacity: 0.4,
            filter: "saturate(60%)",
          },
          "100%": { opacity: 1 },
        })} 2s linear infinite`,
      },
    },
  },
});
